import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import Card, { CardHeader, CardContent } from "@/components/shared/ui/Card";
import { ExternalLink, FileText, X } from "lucide-react";

interface ResearchItem {
  id: string;
  title: string;
  url: string;
  description?: string;
  ai_summary?: string;
  document_url?: string;
  category?: string;
  display_order: number;
  published_date?: string;
}

interface ResearchWidgetProps {
  projectId: string;
}

export default function ResearchWidget({ projectId }: ResearchWidgetProps) {
  const [items, setItems] = useState<ResearchItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<ResearchItem | null>(null);

  useEffect(() => {
    fetchResearchItems();
  }, [projectId]);

  const getCategoryColor = (category?: string) => {
    const colors: Record<string, string> = {
      "Thought Leadership / Research": "bg-purple-500",
      "White Paper": "bg-blue-500",
      Article: "bg-green-500",
      "Exemplar / Case Study": "bg-amber-500",
      Data: "bg-red-500",
    };
    return colors[category || ""] || "bg-gray-500";
  };

  const fetchResearchItems = async () => {
    try {
      setLoading(true);

      const { data, error: fetchError } = await supabase
        .from("project_research")
        .select(
          `
          research_items (
            id,
            title,
            url,
            description,
            ai_summary,
            document_url,
            category,
            display_order,
            published_date
          )
        `
        )
        .eq("project_id", projectId);

      if (fetchError) throw fetchError;

      // Extract research items from nested structure and sort by date (newest first)
      const extractedItems = (data || [])
        .map((item: any) => item.research_items)
        .filter((item: any) => item !== null)
        .sort((a: ResearchItem, b: ResearchItem) => {
          // Sort by published_date first (newest to oldest)
          if (a.published_date && b.published_date) {
            return (
              new Date(b.published_date).getTime() -
              new Date(a.published_date).getTime()
            );
          }
          // If no dates, fall back to display_order
          return a.display_order - b.display_order;
        });

      setItems(extractedItems);
    } catch (err) {
      console.error("Error fetching research items:", err);
      setError("Failed to load research");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card variant="bordered">
        <CardHeader>
          <h3 className="text-lg sm:text-xl font-bold text-black">
            Research Library
          </h3>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 sm:py-8 text-gray-500 text-sm sm:text-base">
            Loading research...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || items.length === 0) {
    return null;
  }

  return (
    <>
      <Card variant="bordered">
        <CardHeader color="green">
          <h3 className="text-lg sm:text-xl font-bold text-black">
            Research Library
          </h3>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">
            Curated research, reports, and articles
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 sm:space-y-4 max-h-[400px] overflow-y-auto">
            {items.map((item) => {
              return (
                <div
                  key={item.id}
                  className="border-2 border-black rounded-lg p-3 sm:p-4 bg-white"
                >
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-r from-blue-400 to-blue-600 flex items-center justify-center">
                      <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {/* Category Tag & Action Buttons */}
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div>
                          {item.category && (
                            <span
                              className={`inline-block px-2 py-1 text-xs font-bold text-white rounded border-2 border-black ${getCategoryColor(
                                item.category
                              )}`}
                            >
                              {item.category}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {item.ai_summary && (
                            <button
                              onClick={() => setSelectedItem(item)}
                              className="px-3 py-1.5 bg-blue-500 text-white text-xs font-bold rounded border-2 border-black hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
                            >
                              Summary
                            </button>
                          )}
                          {item.document_url && (
                            <a
                              href={item.document_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-500 text-white text-xs font-bold rounded border-2 border-black hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <FileText className="w-3 h-3" />
                              View PDF
                            </a>
                          )}
                        </div>
                      </div>

                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex-1">
                          <h4 className="text-sm sm:text-base font-bold text-black break-words">
                            {item.title}
                          </h4>
                          {item.published_date && (
                            <div className="text-xs text-gray-500 mt-1">
                              {new Date(item.published_date).toLocaleDateString(
                                "en-GB",
                                {
                                  month: "short",
                                  year: "numeric",
                                }
                              )}
                            </div>
                          )}
                        </div>
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-600 hover:text-black transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                      {item.description && (
                        <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                          {item.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Summary Modal */}
      {selectedItem && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedItem(null)}
        >
          <div
            className="bg-white rounded-lg border-4 border-black max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-green-200 border-b-4 border-black p-4 sm:p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-blue-400 to-blue-600 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl sm:text-2xl font-bold text-black mb-2">
                    {selectedItem.title}
                  </h2>
                  <a
                    href={selectedItem.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-black hover:underline"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View Original Source
                  </a>
                </div>
                <button
                  onClick={() => setSelectedItem(null)}
                  className="flex-shrink-0 p-2 hover:bg-black hover:bg-opacity-10 rounded transition-colors"
                  aria-label="Close"
                >
                  <X className="w-6 h-6 text-black" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-4 sm:p-6">
              <div className="text-xs font-bold text-gray-600 uppercase mb-3">
                AI Summary
              </div>
              <div className="text-sm sm:text-base text-gray-700 leading-relaxed whitespace-pre-wrap">
                {selectedItem.ai_summary}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
